import type { FormViewT, PageViewT } from "@patient-watch/types";
import {
    findNextUnansweredPageId,
    findSkippedInputIds,
    formHasPageLogic,
    hasVisibilityCondition,
    getFirstPageId,
    pageIdToPageIndex,
    resolveNextAskedPageId,
    type FormAnswerMap,
    type PageLogicPageWithInputs,
} from "@patient-watch/forms";
import { useEffect, useMemo } from "react";

import { useFormNavigationStore } from "./useFormNavigationStore";

function toPageRefs(pages: PageViewT[]): PageLogicPageWithInputs[] {
    return pages.map((page) => ({
        page_id: page.page_id!,
        page_index: page.page_index,
        page_logic: page.page_logic as PageLogicPageWithInputs["page_logic"],
        inputs: page.inputs,
    }));
}

function mergeAnswers(formData: FormAnswerMap, storedAnswers: FormAnswerMap): FormAnswerMap {
    return { ...storedAnswers, ...formData };
}

export function buildAnswerMapFromEntryValues(
    entryValues: Array<{
        input_id: string;
        text_value?: string | null;
        number_value?: number | null;
        text_array?: string[] | null;
    }>
): FormAnswerMap {
    const answers: FormAnswerMap = {};
    for (const value of entryValues) {
        if (value.text_array?.length) {
            answers[value.input_id] = value.text_array;
        } else if (value.number_value != null) {
            answers[value.input_id] = value.number_value;
        } else if (value.text_value != null) {
            answers[value.input_id] = value.text_value;
        }
    }
    return answers;
}

/**
 * `editMode`: editing one page of a finished entry. The edited page is the last unless the edit opens a page
 * with unanswered questions (a changed branch), so Save returns to the results instead of walking the form.
 */
export function useFormBranchingNavigation(
    form: FormViewT | undefined,
    pageIndex: number,
    entryId?: string,
    editMode = false
) {
    const pages = (form?.pages ?? []) as PageViewT[];
    const pageRefs = useMemo(() => toPageRefs(pages), [pages]);
    const hasBranching = useMemo(() => formHasPageLogic(pageRefs), [pageRefs]);
    const hasConditionalInputs = useMemo(
        () => pageRefs.some((page) => (page.inputs ?? []).some((input) => hasVisibilityCondition(input))),
        [pageRefs]
    );
    /**
     * Whether the next page depends on the answers: page logic, or questions that can be hidden (a page whose
     * questions are all hidden is skipped). Other forms simply go page by page.
     */
    const navigatesByAnswers = editMode || hasBranching || hasConditionalInputs;

    const currentPage = pages.find((page) => page.page_index === pageIndex);
    const currentPageId = currentPage?.page_id ?? null;

    const getHistory = useFormNavigationStore((state) => state.getHistory);
    const pushPage = useFormNavigationStore((state) => state.pushPage);
    const popPage = useFormNavigationStore((state) => state.popPage);
    const setHistory = useFormNavigationStore((state) => state.setHistory);
    const resetHistory = useFormNavigationStore((state) => state.resetHistory);

    useEffect(() => {
        if (!navigatesByAnswers || !entryId || !currentPageId) return;
        const history = getHistory(entryId);
        // An edit starts on the page being edited, not the form's first page.
        const startPageId = editMode ? currentPageId : getFirstPageId(pageRefs);
        if (history.length === 0 && startPageId) {
            setHistory(entryId, [startPageId]);
        }
    }, [currentPageId, editMode, entryId, getHistory, navigatesByAnswers, pageRefs, setHistory]);

    const nextUnansweredPageId = (formData: FormAnswerMap, storedAnswers: FormAnswerMap) =>
        currentPageId ? findNextUnansweredPageId(currentPageId, pageRefs, mergeAnswers(formData, storedAnswers)) : null;

    const resolveNextPageIndex = (formData: FormAnswerMap, storedAnswers: FormAnswerMap) => {
        if (editMode) {
            const nextPageId = nextUnansweredPageId(formData, storedAnswers);
            if (!nextPageId) return null;
            if (entryId) pushPage(entryId, nextPageId);
            return pageIdToPageIndex(nextPageId, pageRefs);
        }
        if (!navigatesByAnswers || !currentPageId) {
            return pageIndex < pages.length ? pageIndex + 1 : null;
        }

        const answers = mergeAnswers(formData, storedAnswers);
        const resolution = resolveNextAskedPageId(currentPageId, pageRefs, answers);
        if (resolution.kind === "end") return null;
        const nextIndex = pageIdToPageIndex(resolution.page_id, pageRefs);
        if (entryId && nextIndex != null) pushPage(entryId, resolution.page_id);
        return nextIndex;
    };

    const resolvePreviousPageIndex = () => {
        if (!navigatesByAnswers || !entryId) {
            return pageIndex > 1 ? pageIndex - 1 : null;
        }

        const previousPageId = popPage(entryId);
        if (!previousPageId) return pageIndex > 1 ? pageIndex - 1 : null;
        return pageIdToPageIndex(previousPageId, pageRefs);
    };

    const isLastPage = (formData: FormAnswerMap, storedAnswers: FormAnswerMap) => {
        if (editMode) return nextUnansweredPageId(formData, storedAnswers) === null;
        if (!navigatesByAnswers || !currentPageId) {
            return pageIndex >= pages.length;
        }
        const answers = mergeAnswers(formData, storedAnswers);
        const resolution = resolveNextAskedPageId(currentPageId, pageRefs, answers);
        return resolution.kind === "end";
    };

    /**
     * Inputs these answers do not ask: on skipped pages, or hidden by show_when. Empty for a form with
     * neither page logic nor conditional inputs, where every input is asked.
     */
    const skippedInputIds = (answers: FormAnswerMap): string[] =>
        navigatesByAnswers ? findSkippedInputIds(pageRefs, answers) : [];

    const historyLength = entryId ? getHistory(entryId).length : 0;
    // Linear forms never write this history, so an empty stack must not look like
    // "still on the first page" while the URL is on a later page.
    const visitedPageCount = historyLength > 0 ? historyLength : pageIndex;

    return {
        hasBranching,
        navigatesByAnswers,
        currentPageId,
        resolveNextPageIndex,
        resolvePreviousPageIndex,
        isLastPage,
        skippedInputIds,
        visitedPageCount,
        resetNavigation: () => {
            if (entryId) resetHistory(entryId);
        },
    };
}
